// ============================================================
// GTFS-RT VehiclePosition のポーリング (都営バス)
// ============================================================

import { transit_realtime } from 'gtfs-realtime-bindings'
import type { Entity } from '../types'
import { POLL_VEHICLE_MS } from '../config'
import { fetchToeiBusVehiclePositions } from './odpt-client'
import type { GtfsStatic } from './gtfs-static'

export type VehicleHandler = (vehicles: Entity[]) => void

export class GtfsRtPoller {
  private timer: ReturnType<typeof setInterval> | null = null
  private handlers = new Set<VehicleHandler>()
  private lastVehicles: Entity[] = []
  private inFlight = false
  private gtfs: GtfsStatic | null = null

  setGtfsStatic(gtfs: GtfsStatic | null): void {
    this.gtfs = gtfs
  }

  start(): void {
    if (this.timer) return
    void this.pollOnce()
    this.timer = setInterval(() => void this.pollOnce(), POLL_VEHICLE_MS)
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  subscribe(handler: VehicleHandler): () => void {
    this.handlers.add(handler)
    if (this.lastVehicles.length) handler(this.lastVehicles)
    return () => this.handlers.delete(handler)
  }

  // ───────── private ─────────

  private async pollOnce(): Promise<void> {
    if (this.inFlight) return
    this.inFlight = true
    try {
      const buf = await fetchToeiBusVehiclePositions()
      const feed = transit_realtime.FeedMessage.decode(buf)
      const entities: Entity[] = []

      for (const e of feed.entity) {
        const v = e.vehicle
        if (!v?.position) continue
        const lat = v.position.latitude
        const lon = v.position.longitude
        if (lat == null || lon == null) continue

        const tripRouteId = v.trip?.routeId ?? ''
        const tripId = v.trip?.tripId ?? ''
        const vehicleId = v.vehicle?.id ?? e.id ?? ''

        // 友好的なラベルを GTFS Static の routes.txt / trips.txt から引く
        const shortName = this.gtfs?.getRouteName(tripRouteId) ?? ''
        const headsign  = this.gtfs?.getHeadsign(tripId) ?? ''
        const vehLabel  = (v.vehicle?.label ?? '').trim()
        const friendlyLabel =
          shortName  // 都02 のような系統名
            ? `都営バス ${shortName}${headsign ? ` ${headsign}` : ''}`
            : vehLabel || (tripRouteId ? `都営バス ${tripRouteId} 系統` : '都営バス')

        // nextEta は TripUpdate が無いと取れないが、destination/line だけでもセットする
        const nextEta = headsign || tripRouteId ? {
          line: shortName || tripRouteId,
          dest: headsign,
          mins: 99,  // ETA 不明 sentinel
        } : undefined

        entities.push({
          id: `bus:${vehicleId}:${tripRouteId}`,
          kind: 'bus',
          lat,
          lon,
          moving: (v.position.speed ?? 0) > 0.5,
          bearing: v.position.bearing ?? undefined,
          label: friendlyLabel,
          nextEta,
        })
      }

      this.lastVehicles = entities
      this.handlers.forEach((h) => h(entities))
    } catch (err) {
      console.warn('[gtfs-rt-poller] failed', err)
    } finally {
      this.inFlight = false
    }
  }
}
